<template>
  <div class="totals-display">
    <div class="totals-cnt">
      <ul v-if="props.showAvailabilityLegends" class="availability-legends">
        <li class="green-dot">
          <p>{{ $t('matrix.availability_label_item_available') }}</p>
        </li>
        <li class="red-dot">
          <p>{{ $t('matrix.availability_label_on_backorder') }}</p>
        </li>
        <li class="special-red-dot">
          <p>{{ $t('matrix.availability_label_not_available') }}</p>
        </li>
        <li class="orange-dot">
          <p>{{ $t('matrix.availability_label_low_availability') }}</p>
        </li>
      </ul>
      <div v-if="loadingProductPrices" class="totals loading-wrapper">
        <BackstageIcon :name="Icons.CIRCLE_LOADING" />
      </div>
      <ul v-if="showPriceInfoAfterLoadedSuccessOnProductMatrix" class="totals">
        <li v-if="showWholesalePrice">
          <p>{{ $t('matrix.total_wholesale_price') }}</p>
          <span> {{ formatCurrency(productPriceCurrency, allGroupTotalWholesalePrice, false) }} </span>
        </li>
        <li>
          <p>{{ $t('matrix.total_retailer_price') }}</p>
          <span> {{ formatCurrency(productPriceCurrency, allGroupTotalRetailerPrice, false) }} </span>
        </li>
      </ul>

      <ul class="totals">
        <li v-for="(total, groupName) in mainGroupTotals" :key="groupName">
          <p>
            {{ $t('matrix.total') + ' ' + $t(`matrix.${groupName}`) }}
          </p>
          <span>{{ total }}</span>
        </li>
        <li class="clear">
          <p>{{ $t('matrix.total') }}</p>
          <span>{{ props.matrixTotal }}</span>
        </li>
      </ul>
    </div>
    <div v-if="props.overallTotal" class="total-all-cnt">
      <ul class="totals">
        <li class="total-all">
          <p>{{ $t('matrix.total_all') }}</p>
          <span>{{ props.overallTotal }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { useFormat } from '@/composables/format';
import { Icons } from '@/models/icons.enum';
import { useProductPricesStore } from '@/stores/product-prices';
import type { ProductPriceInfoModel } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

const props = defineProps<{
  mainGroupTotals: Record<string, number>;
  matrixTotal: number;
  overallTotal?: number;
  showAvailabilityLegends?: boolean;
  fabricTotals?: Record<string, number>;
  showWholesalePrice: boolean;
}>();

const { formatCurrency } = useFormat();

const {
  productGroupItemPrice,
  productPriceCurrency,
  loadingProductPrices,
  showPriceInfoAfterLoadedSuccessOnProductMatrix,
} = storeToRefs(useProductPricesStore());

const calculateTotal = (priceKey: keyof ProductPriceInfoModel): number => {
  const keys = Object.keys(props.fabricTotals || {});
  if (keys.length === 0) {
    return 0;
  }

  return keys.reduce((sum, key) => {
    const price = productGroupItemPrice.value[key];

    return sum + (price?.[priceKey] ?? 0) * props.fabricTotals![key];
  }, 0);
};

const allGroupTotalRetailerPrice = computed(() => {
  return calculateTotal('retailer');
});

const allGroupTotalWholesalePrice = computed(() => {
  return calculateTotal('wholesale');
});
</script>

<style lang="scss" scoped>
.totals-cnt {
  column-gap: 16px;
}
.totals-display {
  .total-all-cnt {
    border-top: 1px solid var(--border-color);
  }
}

.availability-legends {
  flex-grow: 1;
}

.totals:not(:first-child) {
  margin-left: 0;
}

.totals:first-child {
  margin-left: auto;
}

.loading-wrapper {
  display: flex;
  justify-content: center;
  height: 53px;
  align-items: center;
}
</style>
